/**
 * Formatação pt-BR do dashboard do vendedor (mockups 07/08). Puro — sem I/O,
 * sem dependência de `api`/React Query — testável isoladamente.
 */
import type { GamificationLevel } from '@troia-v3/types';

import type { PeriodPickerValue } from '@/shared/components/ui';

/** Períodos prontos do seletor (Hoje · Semana · Mês). */
export type SellerPeriod = 'today' | 'week' | 'month';

/** Modo do seletor do dashboard: um período pronto ou o intervalo "Personalizado". */
export type SellerPeriodMode = SellerPeriod | 'custom';

/**
 * Valor do seletor (`PeriodPicker` shared): `{ mode }` para os prontos,
 * `{ mode: 'custom', start, end }` (`yyyy-mm-dd`) para o personalizado.
 */
export type SellerPeriodValue = PeriodPickerValue<SellerPeriod>;

/**
 * Teto do "Personalizado", contando as duas pontas — o mesmo do backend
 * (`SELLER_DASHBOARD_MAX_CUSTOM_DAYS`), que recusa com 422 acima disso.
 */
export const SELLER_CUSTOM_MAX_DAYS = 366;

const PT_MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** Dias de `start` a `end` (`yyyy-mm-dd`), contando as duas pontas. Só datas: a conta é em UTC, sem fuso. */
export function customRangeDays(start: string, end: string): number {
  const ms = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
  return Math.round(ms / 86_400_000) + 1;
}

/** `yyyy-mm-dd` → `dd/mm` (mesmo formato do botão "Personalizado" do `PeriodPicker`). */
const dayMonth = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * Chave do personalizado (`YYYY-MM-DD..YYYY-MM-DD`) → "01/09 - 15/09", com o
 * mesmo traço do `PeriodPicker`. Um dia só vira "01/09". Fora do ano corrente
 * (ou atravessando a virada) leva o ano nas duas pontas — "15/12/2025 -
 * 10/01/2026" —, senão um intervalo do ano passado pareceria deste.
 */
function formatCustomRange(periodKey: string, now: Date): string {
  const [start, end] = periodKey.split('..');
  if (!start || !end) return '';
  const year = String(now.getFullYear());
  const withYear = start.slice(0, 4) !== year || end.slice(0, 4) !== year;
  const fmt = (iso: string): string => (withYear ? `${dayMonth(iso)}/${iso.slice(0, 4)}` : dayMonth(iso));
  return start === end ? fmt(start) : `${fmt(start)} - ${fmt(end)}`;
}

/**
 * Chip do toolbar (mockup 07: "Agosto 2026" · "Semana 34" · "Hoje, 20/08"; o
 * personalizado mostra o intervalo, "01/09 - 15/09"). `now` só pesa no
 * personalizado (decide se o ano aparece).
 */
export function formatPeriodLabel(period: SellerPeriodMode, periodKey: string, now: Date = new Date()): string {
  if (period === 'custom') return formatCustomRange(periodKey, now);
  if (period === 'month') {
    const [y, m] = periodKey.split('-');
    return `${PT_MONTHS[Number(m) - 1] ?? ''} ${y}`.trim();
  }
  if (period === 'week') return `Semana ${Number(periodKey.split('-W')[1] ?? 0)}`;
  const [, m, d] = periodKey.split('-');
  return `Hoje, ${d}/${m}`;
}

/**
 * Mês ANTERIOR ao corrente, em minúsculas — legenda "vs. julho" do KPI "Leads
 * no mês". Esse KPI é sempre do mês corrente, qualquer que seja o período
 * escolhido, então a legenda não pode sair do mês do período: a chave de
 * semana (`2026-W34`) não tem mês (dava "vs. mês anterior") e a do
 * personalizado (`2025-01-01..2025-01-31`) daria o mês do intervalo, não o
 * corrente.
 *
 * As chaves de dia (Hoje) e de mês (Mês) são o "agora" no fuso da EMPRESA e
 * mandam; semana e personalizado caem no relógio do navegador — mesma
 * ressalva de fuso do `formatRelative` (só diverge perto da meia-noite da
 * virada do mês, com o aparelho em outro fuso).
 */
export function previousMonthName(periodKey: string, now: Date = new Date()): string {
  const monthFromKey = /^\d{4}-(\d{2})(?:-\d{2})?$/.exec(periodKey)?.[1];
  const currentIdx = monthFromKey ? Number(monthFromKey) - 1 : now.getMonth();
  return PT_MONTHS[(currentIdx + 11) % 12]?.toLowerCase() ?? 'mês anterior';
}

/**
 * "agora" · "há 2 min" · "há 1 h" · "ontem" · "17/08" (mockup 07, coluna
 * `ld-when`).
 *
 * "agora" cobre o primeiro minuto: logo depois de captar um lead a linha
 * aparecia como "há 0 min", que soa quebrado. Instante no futuro (relógio do
 * cliente atrasado em relação ao servidor) também cai em "agora" em vez de
 * "há -3 min".
 *
 * **Fuso**: "ontem" e o `DD/MM` usam o fuso do NAVEGADOR, não o da empresa.
 * O `SellerDashboardResponse` não carrega o fuso configurado (a janela dos
 * KPIs já vem calculada no backend), então não há como ancorar por empresa
 * aqui sem uma chamada a mais. Na prática o vendedor abre o dashboard no
 * mesmo fuso da loja; o desvio só apareceria com o aparelho configurado em
 * outro fuso, e só nas horas em torno da meia-noite.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = new Date(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'ontem';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Exibição de telefone BR; fora do padrão devolve como veio. NÃO usar pra
 * enviar (NUNCA #77 — matching/envio de telefone tem API própria em contacts).
 * Só formata como BR quando: (a) veio com DDI 55 explícito (>=12 dígitos), ou
 * (b) 10 dígitos (fixo, DDD+8), ou (c) 11 dígitos com o 3º dígito `9`
 * (celular BR pós-9º dígito) — evita formatar números estrangeiros de 11
 * dígitos (ex.: EUA `1` + DDD + número) como se fossem BR.
 */
export function formatPhoneBR(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const hasCountryCode = digits.startsWith('55') && digits.length >= 12;
  const local = hasCountryCode ? digits.slice(2) : digits;
  if (local.length === 11) {
    if (local[2] !== '9') return raw;
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return raw;
}

/**
 * Barra "Meu nível": % de progresso rumo ao próximo nível (0-100) e pontos
 * faltantes. `level` faz parte da assinatura pública (contrato do brief) mas
 * não entra na conta do `pct` — a barra mede a distância até o PISO absoluto
 * do próximo nível (`totalPoints / nextLevel.minPoints`), não a posição
 * relativa dentro da faixa do nível atual.
 */
export function levelProgress(
  totalPoints: number,
  _level: GamificationLevel,
  nextLevel: GamificationLevel | undefined,
): { pct: number; missing: number } {
  if (!nextLevel) return { pct: 100, missing: 0 };
  const pct = Math.min(Math.round((totalPoints / nextLevel.minPoints) * 100), 100);
  const missing = Math.max(nextLevel.minPoints - totalPoints, 0);
  return { pct, missing };
}

export const formatPts = (v: number): string => new Intl.NumberFormat('pt-BR').format(v);
